import { Form, Formik, FormikErrors } from 'formik';
import React, { useCallback, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { FormikTextField, FormikComboField } from '../../../product/components/utils/fields';
import { Btn } from '../../../../components/btn/Btn';
import { orgAddressLine1Validator } from '../../../coordinator/enrollment/steps/validators';
import { PhoneField } from '../../../product/components/FieldPhone';

import { SuggestedAddress } from './SuggestedAddress';

import { addAddress, editAddress } from 'common/api/crm/family/address';
import { Address } from 'common/api/models/Address';
import { stateOptions } from 'common/modules/form-validation/utils/stateOptions';
import {
    addressLine2Validator,
    cityValidator,
    stateValidator,
    zipCodeValidator,
} from 'common/modules/form-validation/validators';
import { createStyles } from 'utils/createStyle';
import { EASE_OUT_QUART, fadeIn } from 'styles/easing';
import { validateAddress } from 'common/api/fulfillment/service';
import { useAlert } from 'modules/alerts';
import { FamilyAddress } from 'common/api/crm/family/address/models/FamilyAddress';
import { waitPromise } from 'utils/promiseHelpers';

export interface ShippingDetailsRequest extends Omit<FamilyAddress, 'id'> {
    id?: string;
}

interface AddressFormOverrides {
    submitBtnText: string;
    addressFormHeader: string;
    personalInfoFormHeader: string;
}

export interface OrderEditAddressFormProps {
    overrides?: AddressFormOverrides;
    addressId?: string;
    request: ShippingDetailsRequest;
    onAddressSaved: (address: FamilyAddress) => void;
    onCancel: () => void;
}

const styles = createStyles({
    formPersonalInformationRow1: [
        'grid',
        {
            gridTemplateColumns: '',
            gridAutoRows: '65px',
            columnGap: '25px',
            '@media(max-width: 640px)': {
                gridTemplateColumns: '',
            },
        },
    ],

    formShippingAddressRow1: [
        'grid',
        {
            gridAutoRows: '65px',
            gridTemplateColumns: '312px auto',
            columnGap: '25px',
            '@media(max-width: 640px)': {
                gridTemplateColumns: '1fr',
            },
            '@media(max-width: 1125px)': {
                gridTemplateColumns: '1fr 0fr',
            },
        },
    ],

    formShippingAddressRow2: [
        'grid',
        {
            gridAutoRows: '65px',
            gridTemplateColumns: 'auto 130px 130px',
            columnGap: '25px',
            '@media(max-width: 640px)': {
                gridTemplateColumns: '1fr 1fr',
            },
        },
    ],

    openFormAnimation: {
        opacity: 0,
        animation: `${fadeIn} 0.3s ${EASE_OUT_QUART} forwards`,
    },
});

export const OrderEditAddressForm = ({
    overrides = {
        submitBtnText: 'Save Address',
        addressFormHeader: 'Shipping Address',
        personalInfoFormHeader: 'Personal Information',
    },
    addressId,
    request,
    onAddressSaved,
    onCancel,
}: OrderEditAddressFormProps) => {
    const validateFormData = (formValues: ShippingDetailsRequest) => {
        const errors: FormikErrors<ShippingDetailsRequest> = {};

        if (!formValues.firstName) {
            errors.firstName = 'First Name is required';
        }
        if (!formValues.lastName) {
            errors.lastName = 'Last Name is required';
        }
        if (!formValues.phoneNumber) {
            errors.phoneNumber = 'Phone Number is required';
        }

        if (!formValues.address1) {
            errors.address1 = 'Address is required';
        }
        if (!formValues.city) {
            errors.city = 'City is required';
        }
        if (!formValues.state) {
            errors.state = 'State is required';
        }
        if (!formValues.zipCode) {
            errors.zipCode = 'ZipCode is required';
        }

        return errors;
    };

    const saveAddress = useCallback(
        async (saveAddressRequest: FamilyAddress) => {
            const response = addressId
                ? await editAddress(saveAddressRequest)
                : await addAddress(saveAddressRequest);

            if (response.response?.ok) {
                onAddressSaved({
                    ...saveAddressRequest,
                    id: ((response.data as string) || '').toString(),
                });
            }
        },
        [onAddressSaved, addressId]
    );

    const alert = useAlert();

    const [suggestedAddress, setSuggestedAddress] = useState<Address | null>();
    const [originalAddress, setOriginalAddress] = useState<FamilyAddress | null>();
    const [isBusy, setIsBusy] = useState(false);

    const onSaveAddress = useCallback(
        async (values: ShippingDetailsRequest) => {
            const saveAddressRequest = {
                ...values,
                id: addressId || '',
                firstName: values.firstName,
                lastName: values.lastName,
                phoneNumber: values.phoneNumber,
            };

            setOriginalAddress(saveAddressRequest);

            const validationResult = await validateAddress(saveAddressRequest);

            if (validationResult.data && validationResult.data.hasSuggestions) {
                setSuggestedAddress(validationResult.data.suggestedAddress);
                return;
            }

            if (validationResult.data && !validationResult.data.wasAddressFound) {
                alert({
                    title: 'Invalid Address',
                    message:
                        'The address you have entered is not valid. Please correct the address and try again.',
                });

                return;
            }

            await saveAddress(saveAddressRequest);
        },
        [setOriginalAddress, setSuggestedAddress, addressId, alert, saveAddress]
    );

    const _onSaveAddress = useCallback(
        (values: ShippingDetailsRequest) => {
            waitPromise(onSaveAddress(values), setIsBusy);
        },
        [onSaveAddress, setIsBusy]
    );

    const onSuggestedAddressConfirm = async (useSuggestedAddress: boolean) => {
        if (!originalAddress || !suggestedAddress) {
            return;
        }

        const address: FamilyAddress = useSuggestedAddress
            ? {
                  id: '',
                  firstName: originalAddress.firstName,
                  lastName: originalAddress.lastName,
                  phoneNumber: originalAddress.phoneNumber,
                  ...suggestedAddress,
              }
            : originalAddress;

        await saveAddress(address);
    };

    const stateOptionsList = [
        { text: '', value: '' },
        ...stateOptions.map((opt) => ({ text: opt.label, value: opt.value })),
    ];

    return (
        <div className={styles.openFormAnimation}>
            {isBusy && (
                <div>
                    <Skeleton height={30} />
                    <Skeleton height={200} />
                    <div className="my-4" />
                    <Skeleton height={30} />
                    <Skeleton height={135} />
                    <div className="my-2" />
                    <Skeleton height={45} width={170} className="mr-2" />
                    <Skeleton height={45} width={170} />
                </div>
            )}
            <div style={{ display: isBusy ? 'none' : '' }}>
                <Formik
                    initialValues={request}
                    onSubmit={_onSaveAddress}
                    validate={validateFormData}
                >
                    {(formikProps) => (
                        <Form>
                            <div>
                                <div>
                                    <div className="text-brand-dark text-lg font-bold mb-1">
                                        {overrides.personalInfoFormHeader}
                                    </div>
                                    <div className={styles.formPersonalInformationRow1}>
                                        <div>
                                            <FormikTextField<ShippingDetailsRequest>
                                                name="firstName"
                                                title={'First Name'}
                                                label={'First Name'}
                                                placeholder="First Name"
                                                error={formikProps.errors.firstName}
                                                required={true}
                                            />
                                        </div>
                                        <div>
                                            <FormikTextField<ShippingDetailsRequest>
                                                name="lastName"
                                                title={'Last Name'}
                                                label={'Last Name'}
                                                placeholder="Last Name"
                                                error={formikProps.errors.lastName}
                                                required={true}
                                            />
                                        </div>
                                        <div>
                                            <PhoneField
                                                name="phoneNumber"
                                                title={'Phone Number'}
                                                label={'Phone Number'}
                                                placeholder="Phone Number"
                                                error={formikProps.errors.phoneNumber}
                                                required={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <div className="text-brand-dark text-lg font-bold mb-1">
                                        {overrides.addressFormHeader}
                                    </div>
                                    <div className="grid grid-cols-1 gap-1">
                                        <div className={styles.formShippingAddressRow1}>
                                            <div className="inline-block align-top">
                                                <FormikTextField
                                                    name="address1"
                                                    label="Address"
                                                    placeholder="1234 Street Name"
                                                    validate={orgAddressLine1Validator}
                                                    error={formikProps.errors.address1}
                                                    required={true}
                                                />
                                            </div>
                                            <div className="inline-block align-top w-full">
                                                <FormikTextField
                                                    name="address2"
                                                    label="Address/Suite/Other"
                                                    placeholder=""
                                                    validate={addressLine2Validator}
                                                    error={formikProps.errors.address2}
                                                />
                                            </div>
                                        </div>
                                        <div className={styles.formShippingAddressRow2}>
                                            <div className="inline-block align-top w-full col-span-2 sm:col-span-1">
                                                <FormikTextField
                                                    name="city"
                                                    label="City"
                                                    placeholder="City Name"
                                                    validate={cityValidator}
                                                    required={true}
                                                    error={formikProps.errors.city}
                                                />
                                            </div>
                                            <div>
                                                <FormikComboField
                                                    label="State"
                                                    name="state"
                                                    validate={stateValidator}
                                                    required={true}
                                                    options={stateOptionsList}
                                                    error={formikProps.errors.state}
                                                />
                                            </div>
                                            <div className="inline-block align-top ">
                                                <FormikTextField
                                                    name="zipCode"
                                                    label={'Zip'}
                                                    placeholder="12345"
                                                    required={true}
                                                    maxLength={10}
                                                    validate={zipCodeValidator}
                                                    error={formikProps.errors.zipCode}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="text-left mt-4">
                                <div className="block">
                                    <div className="sm:hidden flex flex-col gap-4 justify-center w-full">
                                        <div className="text-center">
                                            <Btn type="submit" className="max-w-xs w-full">
                                                {overrides.submitBtnText}
                                            </Btn>
                                        </div>
                                        <div className="text-center">
                                            <Btn
                                                type="button"
                                                className="max-w-xs w-full"
                                                isSecondary={true}
                                                onClick={onCancel}
                                            >
                                                Cancel
                                            </Btn>
                                        </div>
                                    </div>
                                    <div className="hidden sm:flex text-left gap-4">
                                        <Btn type="button" isSecondary={true} onClick={onCancel}>
                                            Cancel
                                        </Btn>
                                        <Btn type="submit" className="hidden sm:inline-block">
                                            {overrides.submitBtnText}
                                        </Btn>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
            {suggestedAddress && originalAddress && (
                <SuggestedAddress
                    suggestedAddress={suggestedAddress}
                    originalAddress={originalAddress}
                    onConfirm={onSuggestedAddressConfirm}
                    onClose={() => setSuggestedAddress(null)}
                />
            )}
        </div>
    );
};
